import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../api/client';
import { PageHeader } from '../../components/ui/PageHeader';
import { formatEUR } from '../../lib/format';
import { cn } from '../../utils/cn';
import {
  FINAL,
  PortalReturnTabs,
  StatusBadge,
  Track,
  type ReturnDetail,
  type ReturnRow,
} from '../../components/returns/returnsShared';

/**
 * The garage portal's returns (returns v2): every return in three steps — the request sent, the
 * part taken in at MECADEPOT, the quality control that decides what is credited. In progress /
 * finished; a click opens the return.
 */
export default function PortalReturns() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tab, setTab] = useState<'OPEN' | 'DONE'>('OPEN');
  const [rows, setRows] = useState<ReturnRow[] | null>(null);
  const [details, setDetails] = useState<Record<string, ReturnDetail>>({});

  useEffect(() => {
    apiClient
      .get<ReturnRow[]>('/returns/board')
      .then(async (r) => {
        setRows(r.data);
        const out: Record<string, ReturnDetail> = {};
        await Promise.all(
          r.data
            .filter((x) => !x.legacy)
            .slice(0, 40)
            .map(async (x) => {
              out[x.id] = (await apiClient.get<ReturnDetail>(`/returns/${x.id}/full`)).data;
            })
        );
        setDetails(out);
      })
      .catch(() => setRows([]));
  }, []);

  const open = (rows ?? []).filter((r) => !FINAL.includes(r.status));
  const done = (rows ?? []).filter((r) => FINAL.includes(r.status));
  const list = tab === 'OPEN' ? open : done;

  return (
    <div className="app-tokens">
      <PortalReturnTabs />
      <PageHeader
        title={t('returnsV2.portal.title')}
        description={t('returnsV2.portal.description')}
      />
      <div className="mb-3 flex flex-wrap gap-2" role="radiogroup">
        {(['OPEN', 'DONE'] as const).map((k) => (
          <label
            key={k}
            className={cn(
              'flex cursor-pointer items-center gap-2 rounded-md border bg-card px-3 py-1.5 text-sm',
              tab === k && 'border-primary bg-primary-soft'
            )}
          >
            <input type="radio" name="trk" checked={tab === k} onChange={() => setTab(k)} />
            {t(`returnsV2.portal.tab.${k}`, { n: k === 'OPEN' ? open.length : done.length })}
          </label>
        ))}
      </div>
      {rows === null && (
        <p className="px-4 py-12 text-center text-sm text-muted-foreground">
          {t('returnsV2.loading')}
        </p>
      )}
      {rows && !list.length && (
        <div className="rounded-lg border bg-card px-4 py-12 text-center text-sm text-muted-foreground shadow-sm">
          {tab === 'OPEN' ? t('returnsV2.portal.emptyOpen') : t('returnsV2.portal.emptyDone')}
        </div>
      )}
      {list.map((r) => (
        <section
          key={r.id}
          role="link"
          tabIndex={0}
          onClick={() => navigate(`/b2b/returns/${r.id}`)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') navigate(`/b2b/returns/${r.id}`);
          }}
          className="mb-3 cursor-pointer rounded-lg border bg-card shadow-sm hover:border-muted-foreground"
          aria-label={t('returnsV2.open') + ' ' + r.returnNumber}
        >
          <div className="flex flex-wrap items-center gap-3 border-b px-4 py-2.5">
            <h2 className="flex-1 text-sm font-semibold">
              <span className="font-mono">{r.returnNumber}</span> ·{' '}
              {r.lines.map((l) => `${l.sku} × ${l.qty}`).join(', ')}
            </h2>
            <StatusBadge status={r.status} refundInProgress={r.refundInProgress} />
            <span className="text-sm tabular-nums text-muted-foreground">
              {formatEUR(r.totalTtc)}
            </span>
          </div>
          {details[r.id] && <Track r={details[r.id]} compact />}
        </section>
      ))}
    </div>
  );
}
